// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { SkeletonText } from '@carbon/react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Carousel from 'react-multi-carousel';

import useScreen from 'Hooks/useScreen';
import ButtonGroup from 'Components/Dashboard/CarouselButtonGroup';

const StyledNotifications = styled.section`
  position: relative;
  margin-top: 75px;
  padding-bottom: 25px;

  h2 {
    text-transform: none;
    font-weight: 600;;
  }

  .notificationSlider {
    margin: 0 -16px;

    .react-multi-carousel-item {
      display: flex;
    }

    .notification {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 0 16px;
      width: 100%;

      .content {
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      button {
        border: none;
        background: none;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
      }

      .notificationHeader {
        border-bottom: 1px solid #717171;
        padding-bottom: 16px;
        margin-bottom: 16px;

        h3 {
          color: #686868;
          font-size: 21px;
          font-weight: 600;
        }

        .date {
          display: inline-block;
          color: #717171;
          font-size: 14px;
          margin-top: 8px;
        }
      }

      .notificationContent {
        display: flex;
        flex-direction: column;
        flex: 1;
        align-items: start;
        color: #717171;
        line-height: 1.5;
        padding-bottom: 16px;

        button {
          padding: 8px 0;
          color: #F47926;
        }
      }

      button.primary {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: auto;
        width: auto;
        max-width: 100%;
        gap: 8px;
        background: #F47926;
        color: #fff;
        border-radius: 2px;
        padding: 8px 32px;
      }
    }
  }

  .skeletonNotificationBtn {
    margin-top: 32px;
    aspect-ratio: 9;
    height: auto;
  }
`;

const Notifications = ({
  notifications,
  notificationsLoading,
  handleReadMore,
  handleDownload,
  handleFileDownload,
}) => {
  const { t } = useTranslation();
  const { width } = useScreen();

  const renderNotificationSkeleton = a => (
    <div key={ a } className="notification">
      <div className="content" style={ { width: '100%' } }>
        <div className="notificationHeader">
          <h3><SkeletonText /></h3>
          <span className="date"><SkeletonText /></span>
        </div>
        <div className="notificationContent">
          <span style={ { width: '100%' } }><SkeletonText paragraph /></span>
        </div>
      </div>
      <SkeletonText className="skeletonNotificationBtn" />
    </div>
  );

  return (
    <StyledNotifications id="notifications">
      <h2>{ t('pages.recentNotifications') }</h2>
      <div className="notificationsWrapper">
        <Carousel
          className="notificationSlider"
          ssr
          customButtonGroup={ <ButtonGroup /> }
          autoPlaySpeed={ 3000 }
          deviceType="desktop"
          showDots={ false }
          autoPlay={ false }
          renderButtonGroupOutside
          arrows={ false }
          slidesToSlide={ 1 }
          removeArrowOnDeviceType={ ['tablet', 'mobile'] }
          shouldResetAutoplay={ width > 767 }
          responsive={ {
            desktop: {
              breakpoint: { max: 3000, min: 1540 },
              items: 3
            },
            tablet: {
              breakpoint: { max: 1539, min: 767 },
              items: 2
            },
            mobile: {
              breakpoint: { max: 767, min: 0 },
              items: 1,
            }
          } }
        >
          { notificationsLoading ? ([...Array(3).keys()].map(a => renderNotificationSkeleton(a))) : (
            notifications.map(notification => (
              <div
                key={ notification.messageId }
                className="notification"
              >
                <div className="content">
                  <div className="notificationHeader">
                    <h3>{ notification.title }</h3>
                    <span className="date">{ notification.startDate }</span>
                  </div>
                  <div className="notificationContent">
                    <span>{ notification.shortContent || notification.content }</span>
                    { notification.shortContent && (
                      <button
                        type="button"
                        className="readMore"
                        onClick={ () => handleReadMore(notification.messageId) }
                      >
                        { t('pages.readMore') }
                      </button>
                    )}
                  </div>
                </div>
                { notification.filename && (
                  <button
                    type="button"
                    kind="ghost"
                    className="primary"
                    onClick={ () => {
                      if (notification?.messageId === 0) {
                        handleDownload(notification?.filename);
                      } else {
                        handleFileDownload(notification.messageId);
                      }
                    } }
                  >
                    <Download />
                    { t('pages.download') }
                  </button>
                ) }
              </div>
            ))
          ) }
        </Carousel>
      </div>
    </StyledNotifications>
  );
};

Notifications.propTypes = {
  notifications: arrayOf(shape({
    messageId: number.isRequired,
    title: string.isRequired,
    content: string.isRequired,
    shortContent: string,
    startDate: string.isRequired,
    filename: string,
  })).isRequired,
  notificationsLoading: bool.isRequired,
  handleReadMore: func.isRequired,
  handleDownload: func.isRequired,
  handleFileDownload: func.isRequired,
};

export default Notifications;
